/**
 * Enhanced component for selecting a date from a visually appealing calendar
 */
import React, { useState } from 'react';
import { useTeeTime } from '../hooks/useTeeTime';
import { getDateRange, formatDateShort, getDayOfWeek, isSameDay, formatDateLong } from '../utils/dateUtils';
import '../styles/DateSelector.css';

/**
 * DateSelector component props
 */
interface DateSelectorProps {
  numberOfDays?: number;
}

/**
 * Returns an icon representing the current weather for a date
 * This is for visual enhancement only - actual weather data would come from an API
 * @param date The date to get weather for
 * @returns An emoji representing the weather
 */
const getWeatherIconForDate = (date: Date): string => {
  // Using the date's day to generate pseudo-random but consistent weather for demo
  const day = date.getDate();
  const icons = ['☀️', '⛅', '🌤️', '☁️', '🌧️', '⛈️', '🌦️'];
  return icons[day % icons.length];
};

/**
 * Returns a weather description for a date
 * @param date The date to get weather for
 * @returns Weather description text
 */
const getWeatherDescription = (date: Date): string => {
  const day = date.getDate();
  const descriptions = [
    'Sunny, perfect golf weather',
    'Partly cloudy, great conditions',
    'Mostly sunny, light breeze',
    'Cloudy but dry',
    'Light rain possible',
    'Scattered thunderstorms',
    'Passing showers'
  ];
  return descriptions[day % descriptions.length];
};

/**
 * Returns a temperature for a date (for demo purposes)
 * @param date The date to get temperature for
 * @returns Temperature in Fahrenheit
 */
const getTemperature = (date: Date): number => {
  const day = date.getDate();
  // Generate temperatures between 65-85°F
  return 65 + (day % 20);
};

/**
 * Enhanced DateSelector component for selecting a date from a visual calendar
 * @param props Component props
 * @returns React component
 */
const DateSelector: React.FC<DateSelectorProps> = ({ numberOfDays = 14 }) => {
  const { selectedDate, setSelectedDate } = useTeeTime();
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  
  // Generate date range for the calendar
  const dateRange = getDateRange(new Date(), numberOfDays);
  
  /**
   * Handles mouse entering a date card
   * @param date The date being hovered
   */
  const handleMouseEnter = (date: Date) => {
    setHoveredDate(date);
  };
  
  /**
   * Handles mouse leaving a date card
   */
  const handleMouseLeave = () => {
    setHoveredDate(null);
  };
  
  /**
   * Gets a CSS class for a date based on the date's day of week
   * @param date The date to check
   * @returns CSS class name
   */
  const getDateTypeClass = (date: Date): string => {
    const day = date.getDay();
    if (day === 0 || day === 6) {
      return 'weekend';
    }
    return 'weekday';
  };
  
  /**
   * Gets a label for today
   * @param date The date to check
   * @returns 'Today' if the date is today, empty string otherwise
   */
  const getTodayLabel = (date: Date): string => {
    const today = new Date();
    return isSameDay(date, today) ? 'Today' : '';
  };
  
  return (
    <div className="date-selector">
      <div className="date-selector-header">
        <h2>Select a Date</h2>
        {hoveredDate && (
          <div className="date-preview">
            <div className="preview-date">{formatDateLong(hoveredDate)}</div>
            <div className="preview-weather">
              <span className="weather-icon">{getWeatherIconForDate(hoveredDate)}</span>
              <span className="weather-temp">{getTemperature(hoveredDate)}°F</span>
              <span className="weather-desc">{getWeatherDescription(hoveredDate)}</span>
            </div>
          </div>
        )}
      </div>
      
      <div className="date-selector-grid">
        {dateRange.map((date) => (
          <div
            key={date.toISOString()}
            className={`date-selector-item ${getDateTypeClass(date)} ${
              isSameDay(date, selectedDate) ? 'date-selector-item-selected' : ''
            }`}
            onClick={() => setSelectedDate(date)}
            onMouseEnter={() => handleMouseEnter(date)}
            onMouseLeave={handleMouseLeave}
          >
            <div className="date-selector-day">{getDayOfWeek(date)}</div>
            <div className="date-selector-date">{formatDateShort(date)}</div>
            {getTodayLabel(date) && <div className="today-label">Today</div>}
            <div className="date-selector-weather">
              <span className="mini-weather-icon">{getWeatherIconForDate(date)}</span>
              <span className="mini-weather-temp">{getTemperature(date)}°</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DateSelector;
